import { Box, Card, CardTabs } from 'vopay';
import { FunctionComponent, useEffect, useState, JSX, useMemo } from 'react';
import { Title } from 'UI';
import { useLocation, useHistory } from 'react-router-dom';
import { usePermissions } from '@/providers/Permissions.provider';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import { Button } from 'UI';
import { useTranslation } from 'react-i18next';
import FileUploadDrawer from './FileUploadDrawer/FileUploadDrawer';
import BulkUploadTable from './BulkUploadTable';
import FileConversionTable from './FileConversionTable';

type FC = FunctionComponent;

const FileUploadHistory: FC = () => {
  const { permissions } = usePermissions();
  const { FileConversionEnabled } = useLockedFeatures();
  const location = useLocation();
  const history = useHistory();
  const { t } = useTranslation('translations', { keyPrefix: 'FileUpload.History' });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  type TabContents = {
    bulkUpload: JSX.Element;
    fileConversion: JSX.Element;
  };

  // Get tab from query params or default to bulkUpload
  const queryTab = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    return tab === 'fileConversion' ? 'fileConversion' : 'bulkUpload';
  }, [location.search]);

  const [activeTab, setActiveTab] = useState<keyof TabContents>(queryTab);

  // Update activeTab when queryTab changes
  useEffect(() => {
    setActiveTab(queryTab);
  }, [queryTab]);

  const handleTabChange = (value: string) => {
    if (value === 'bulkUpload' || value === 'fileConversion') {
      setActiveTab(value);
      // Clear all query params and set only the tab param
      history.push(`${location.pathname}?tab=${value}`);
    }
  };

  // Check if we should open the drawer when navigated from file upload confirm
  useEffect(() => {
    if (location.state && (location.state as any)?.openDrawer) {
      setIsDrawerOpen(true);
    }
  }, [location.state]);

  // If FileConversionEnabled is false, just show BulkUploadTable without tabs
  if (!FileConversionEnabled) {
    return (
      <Box flx="row" jc="center">
        <Box flx="col" gapSize="36px" width="100%">
          <Box jc="sb" ai="center">
            <Title>{t('pageTitle')}</Title>
            <Box jc="fe">
              {permissions.CreateBulkFileUpload && (
                <Button
                  id="export-group"
                  colorScheme="primary"
                  onClick={() => {
                    setIsDrawerOpen(true);
                  }}
                >
                  {t('fileUploadButton')}
                </Button>
              )}
            </Box>
          </Box>
          <Card
            style={{
              padding: '16px',
            }}
          >
            <BulkUploadTable />
          </Card>
        </Box>
        <FileUploadDrawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} />
      </Box>
    );
  }

  return (
    <Box flx="row" jc="center">
      <Box flx="col" gapSize="36px" width="100%">
        <Box jc="sb" ai="center">
          <Title>{t('pageTitle')}</Title>
          <Box jc="fe">
            {permissions.CreateBulkFileUpload && (
              <Button
                id="export-group"
                colorScheme="primary"
                onClick={() => {
                  setIsDrawerOpen(true);
                }}
              >
                {t('fileUploadButton')}
              </Button>
            )}
          </Box>
        </Box>
        <CardTabs.Root value={activeTab} onValueChange={handleTabChange}>
          <CardTabs.List aria-label="File Upload History">
            <CardTabs.Trigger value="bulkUpload">Bulk Upload</CardTabs.Trigger>
            <CardTabs.Trigger value="fileConversion">File Conversion</CardTabs.Trigger>
          </CardTabs.List>

          <CardTabs.Content value="bulkUpload">
            <BulkUploadTable />
          </CardTabs.Content>

          <CardTabs.Content value="fileConversion">
            <FileConversionTable />
          </CardTabs.Content>
        </CardTabs.Root>
      </Box>
      <FileUploadDrawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} />
    </Box>
  );
};

export default FileUploadHistory;
